import api, { type ErrorType, type PageRes, apiRequest } from "../api";

// ─── Types ────────────────────────────────────────────────────────────────────

export type ConversationStatus = "open" | "closed" | "pending";

export interface Conversation {
  _id: string;
  type: "individual" | "group";
  waId?: string;
  groupId?: string;
  name?: string;
  lastMessageAt?: string;
  lastMessagePreview?: string;
  unreadCount?: number;
  windowExpiresAt?: string;
  status?: ConversationStatus;
  /** Se o contato já definiu e-mail+senha — usado pra só mostrar "Enviar cadastro" quando faz sentido. */
  contactHasAccount?: boolean;
  /** Pessoa (User permissão CLIENT) dona do número — nulo quando o telefone ainda não tem cadastro. */
  contact?: {
    _id: string;
    name?: string;
    email?: string;
    phone?: string;
    tags?: string[];
    companyName?: string;
  } | null;
  /** Quem já viu esta conversa (a caixa é compartilhada pela equipe, então "visto" é por agente). */
  readBy?: Array<{ agentId: string; name: string; readAt: string }>;
}

export interface MessageAttachment {
  url: string;
  name: string;
  mimeType: string;
  size?: number;
  transcript?: string;
}

export type MessageDirection = "inbound" | "outbound";
export type MessageStatus = "sent" | "delivered" | "read" | "failed";

export interface ConversationMessage {
  _id: string;
  conversationId: string;
  direction: MessageDirection;
  fromWaId?: string;
  senderAgentId?: string;
  content: {
    msg?: string;
    attachments?: MessageAttachment[];
  };
  wamid?: string;
  status?: MessageStatus;
  errorReason?: string;
  errorCode?: number;
  contextWamid?: string;
  linkedTicketId?: string;
  reactions?: Array<{ emoji: string; fromWaId: string }>;
  createdAt: string;
  /** Nota interna — nunca saiu pelo WhatsApp, só a equipe vê. */
  isInternal?: boolean;
}

export interface SendMessagePayload {
  msg?: string;
  attachments?: MessageAttachment[];
  contextWamid?: string;
  /** true = nota interna (não sai pelo WhatsApp, ignora janela de 24h). */
  internal?: boolean;
}

export interface SendMessageResponse {
  success: boolean;
  result: ConversationMessage;
}

export interface SendTemplatePayload {
  templateName: string;
  language: string;
  components?: unknown[];
}

export interface CreateTicketPayload {
  from?: string;
  to?: string;
  title?: string;
  priority?: string;
  clientId?: string;
  clientUserId?: string;
  memberIds?: string[];
  messageIds?: string[];
}

export interface CreateTicketResponse {
  success: boolean;
  ticket: {
    _id: string;
    protocolId?: string;
    title?: string;
  };
}

export interface LinkTicketPayload {
  ticketRef: string;
  messageIds: string[];
}

export interface LinkTicketResponse {
  success: boolean;
}

export interface StartConversationPayload {
  clientUserId?: string;
  phone?: string;
  name?: string;
  templateName: string;
  language: string;
  components?: unknown[];
}

export interface StartConversationResponse {
  success: boolean;
  conversation: Conversation;
  result: ConversationMessage;
}

// ─── Service ─────────────────────────────────────────────────────────────────

export const conversationService = {
  /** Inicia uma nova conversa enviando um template aprovado (sem inbound prévio do cliente). */
  async start(
    payload: StartConversationPayload
  ): Promise<[StartConversationResponse | null, ErrorType | null]> {
    return apiRequest<StartConversationResponse>({
      loaderKey: "startConversation",
      method: "post",
      url: "/app/conversations/start",
      data: payload,
    });
  },

  /** Busca uma conversa por id — usado pra abrir direto via link/deep-link. */
  async getById(id: string): Promise<[Conversation | null, ErrorType | null]> {
    return apiRequest<Conversation>({
      loaderKey: "getConversationById",
      method: "get",
      url: `/app/conversations/${id}`,
    });
  },

  async list(params: {
    page?: number;
    limit?: number;
    search?: string;
  } = {}): Promise<[PageRes<Conversation> | null, ErrorType | null]> {
    return apiRequest<PageRes<Conversation>>({
      loaderKey: "listConversations",
      method: "get",
      url: "/app/conversations",
      params,
    });
  },

  async getMessages(
    id: string,
    params: { limit?: number; before?: string } = {}
  ): Promise<[PageRes<ConversationMessage> | null, ErrorType | null]> {
    return apiRequest<PageRes<ConversationMessage>>({
      loaderKey: "getConversationMessages",
      method: "get",
      url: `/app/conversations/${id}/messages`,
      params,
    });
  },

  async send(
    id: string,
    payload: SendMessagePayload
  ): Promise<[SendMessageResponse | null, ErrorType | null]> {
    return apiRequest<SendMessageResponse>({
      loaderKey: "sendConversationMessage",
      method: "post",
      url: `/app/conversations/${id}/send`,
      data: payload,
      ignoreError: true,
    });
  },

  async sendTemplate(
    id: string,
    payload: SendTemplatePayload
  ): Promise<[SendMessageResponse | null, ErrorType | null]> {
    return apiRequest<SendMessageResponse>({
      loaderKey: "sendConversationTemplate",
      method: "post",
      url: `/app/conversations/${id}/send-template`,
      data: payload,
    });
  },

  /** Envia um template de plataforma (não-Meta) — só funciona com a janela de 24h aberta. */
  async sendPlatformTemplate(
    id: string,
    platformTemplateId: string
  ): Promise<[(SendMessageResponse & { warning?: string }) | null, ErrorType | null]> {
    return apiRequest<SendMessageResponse & { warning?: string }>({
      loaderKey: "sendConversationPlatformTemplate",
      method: "post",
      url: `/app/conversations/${id}/send-platform-template`,
      data: { platformTemplateId },
    });
  },

  /** Upload de arquivo (áudio/imagem/vídeo) para anexar a uma mensagem de conversa. */
  async uploadAttachment(file: File): Promise<[MessageAttachment | null, { msg?: string } | null]> {
    const formData = new FormData();
    formData.append("file", file);
    try {
      const res = await api.post<{ url: string; name: string; mimeType: string; size: number }>(
        "/app/conversations/upload",
        formData,
        {
          transformRequest: [
            (data, headers) => {
              if (typeof FormData !== "undefined" && data instanceof FormData) {
                const h = headers as Record<string, string> & { delete?: (k: string) => void };
                if (typeof h?.delete === "function") {
                  h.delete("Content-Type");
                } else {
                  delete h["Content-Type"];
                }
              }
              return data;
            },
          ],
        }
      );
      const d = res.data;
      return [{ url: d.url, name: d.name, mimeType: d.mimeType, size: d.size }, null];
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: { msg?: string }; msg?: string } } };
      const data = err?.response?.data;
      const msg =
        data?.error?.msg ||
        (data && typeof data === "object" && "msg" in data ? (data as { msg: string }).msg : undefined) ||
        "Falha no upload";
      return [null, { msg }];
    }
  },

  async markRead(
    id: string
  ): Promise<[{ success: boolean; readBy?: Conversation["readBy"] } | null, ErrorType | null]> {
    return apiRequest<{ success: boolean; readBy?: Conversation["readBy"] }>({
      loaderKey: "markConversationRead",
      method: "post",
      url: `/app/conversations/${id}/read`,
      ignoreError: true,
    });
  },

  async createTicket(
    id: string,
    payload: CreateTicketPayload
  ): Promise<[CreateTicketResponse | null, ErrorType | null]> {
    return apiRequest<CreateTicketResponse>({
      loaderKey: "createTicketFromConversation",
      method: "post",
      url: `/app/conversations/${id}/create-ticket`,
      data: payload,
    });
  },

  async linkTicket(
    id: string,
    payload: LinkTicketPayload
  ): Promise<[LinkTicketResponse | null, ErrorType | null]> {
    return apiRequest<LinkTicketResponse>({
      loaderKey: "linkTicketToConversation",
      method: "post",
      url: `/app/conversations/${id}/link-ticket`,
      data: payload,
    });
  },

  /** Manda pelo WhatsApp o link para o contato definir e-mail/senha e acessar o portal. */
  async sendClaimLink(
    id: string
  ): Promise<[{ msg: string; requiresWhatsappConfirmation?: boolean } | null, ErrorType | null]> {
    return apiRequest<{ msg: string; requiresWhatsappConfirmation?: boolean }>({
      loaderKey: "sendConversationClaimLink",
      method: "post",
      url: `/app/conversations/${id}/send-claim-link`,
    });
  },
};
